import type { ReactNode } from 'react';
import { Label } from './label.js';
import {
  SearchableSelectField,
  type SearchableSelectFieldOption,
} from './searchable-select-field.js';

export interface LabeledSearchableSelectFieldProps {
  id: string;
  /** Pass a translated string. */
  label: string;
  options: SearchableSelectFieldOption[];
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /** When true, prepends a clear/empty option to the dropdown list. */
  allowEmpty?: boolean;
  /** Label shown for the clear option when allowEmpty is true. Defaults to '—'. */
  emptyLabel?: string;
  error?: boolean;
  testId?: string;
  /** Optional content rendered inline next to the label (e.g. an info/help-dialog trigger). */
  labelSlot?: ReactNode;
}

/** Label + SearchableSelectField pair with the id/htmlFor/data-testid association wired once. */
export function LabeledSearchableSelectField({
  id,
  label,
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  allowEmpty,
  emptyLabel,
  error,
  testId,
  labelSlot,
}: LabeledSearchableSelectFieldProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Label htmlFor={id}>{label}</Label>
        {labelSlot}
      </div>
      <SearchableSelectField
        id={id}
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        emptyText={emptyText}
        allowEmpty={allowEmpty}
        emptyLabel={emptyLabel}
        error={error}
        data-testid={testId}
      />
    </div>
  );
}
